/* eslint-disable */
import { Button, Modal, Space, Typography } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import Icon from 'components/atoms/Icon';
import useWindowDimensions from 'hooks/useWindowDemensions';
// import NotifyModal from 'components/templates/NotifyModal';

interface CameraCaptureProps {
  name?: string;
  isSingle?: boolean;
  loading?: boolean;
  isError?: boolean;
  handleFinish?: (photos: File[]) => void;
  handleCheckFaceId?: (photos: File[]) => void;
  handleBack?: (photos?: File[]) => void;
  handleResetError?: () => void;
  notifySuccess?: {
    title: string;
    desc: string;
  }
  isConfirm?: boolean;
}

export interface CameraCaptureRef {
  handleReCapture: () => void;
}

const CameraCapture = forwardRef<CameraCaptureRef, CameraCaptureProps>(
  ({
    name,
    isSingle,
    loading,
    isError,
    handleFinish,
    handleBack,
    handleResetError,
    isConfirm,
    handleCheckFaceId,
    notifySuccess,
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState(false);
    const [preview, setPreview] = useState('');
    const [photos, setPhotos] = useState<File[]>([]);
    const [errRequest, setErrRequest] = useState(false);
    const [isSupport, setIsSupport] = useState(false);
    const [readyCamera, setReadyCamera] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const { height: windowHeight, width: windowWidth } = useWindowDimensions();

    const stopSelfie = async () => {
      await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
        .then((sm) => {
          if (!videoRef.current) return;
          videoRef.current.srcObject = sm;
          sm.getTracks().forEach((track) => {
            track.stop();
          });
        });
    };

    const handleCapture = useCallback(async () => {
      if (videoRef.current && canvasRef.current) {
        //* Workaround
        const ctx = canvasRef.current.getContext('2d');

        const videoWidth = videoRef.current.videoWidth;
        const videoHeight = videoRef.current.videoHeight;

        const frameWidth = windowWidth > 768 ? videoWidth * 0.7 : videoWidth * 0.8; // Ví dụ: 80% chiều rộng
        const frameHeight = windowWidth > 768 ? videoHeight * 0.74 : videoHeight * 0.5; // 50% chiều cao

        const offsetX = (videoWidth - frameWidth) / 2;
        const offsetY = (videoHeight - frameHeight) / 2;

        canvasRef.current.width = frameWidth;
        canvasRef.current.height = frameHeight;

        ctx?.drawImage(
          videoRef.current,
          offsetX,
          offsetY,
          frameWidth,
          frameHeight,
          0,
          0,
          frameWidth,
          frameHeight,
        );
        const imageData = canvasRef.current.toDataURL('image/jpeg');
        setPreview(imageData);

        canvasRef.current.toBlob(async (b) => {
          if (b) {
            const file = new File([b], `${name}-${photos.length}.jpeg`, { type: 'image/jpeg' });
            setPhotos(isSingle ? [file] : [...photos, file]);
          }
        }, 'image/jpeg');
      }
    }, [
      name,
      photos.length,
      isSingle,
      videoRef,
      canvasRef,
      windowWidth,
    ]);

    const handleReset = () => {
      setPhotos([]);
      setIsOpen(false);
      setPreview('');
      setError(false);
      if (handleResetError) handleResetError();
    };

    const handleSubmit = async () => {
      if (handleFinish) handleFinish(photos);
    };

    const onBack = async () => {
      if (handleBack) {
        handleBack(photos);
      }
      await stopSelfie();
    };

    const closeReview = () => {
      if (isSingle) {
        setPhotos([]);
      }
      setPreview('');
    };

    useImperativeHandle(ref, () => ({
      handleReCapture: () => handleReset(),
    }));

    useEffect(() => {
      setError(!!isError);
      if (isError) {
        setIsOpen(false);
      }
    }, [isError]);

    useEffect(() => {
      if (!errRequest) {
        const streamPromise = navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { max: 30 },
            aspectRatio: 16 / 9,
          },
        }).catch((err: any) => {
          setReadyCamera(false);
          if (err.name === 'NotAllowedError') {
            setErrRequest(true);
          } else {
            setIsSupport(true);
          }
          return null;
        });
        if (videoRef) {
          streamPromise.then((stream) => {
            if (videoRef.current) {
              setReadyCamera(true);
              videoRef.current.srcObject = stream;
            }
          });
        }
        return () => {
          streamPromise.then((stream) => stream?.getTracks().forEach((track) => {
            setReadyCamera(false);
            track.stop();
          }));
        };
      }
      return () => { };
    }, [errRequest]);

    const frame = useMemo(() => {
      // create border width for frame based on window dimensions      
      if (videoRef.current && windowWidth && windowHeight && readyCamera) {
        const frameWidth = windowWidth > 1024 ? windowWidth * 0.7 : windowWidth * 0.85; // Ví dụ: 90% chiều rộng
        const frameHeight = windowWidth > 1024 ? windowHeight * 0.7 : windowHeight * 0.35; // 40% chiều cao
        const offsetX = (windowWidth - frameWidth) / 2; // Convert to rem
        const offsetY = (windowHeight - frameHeight) / 2;

        return {
          width: `${frameWidth}px`,
          height: `${frameHeight}px`,
          left: `${offsetX}px`,
          top: `${offsetY}px`,
        };
      }
      return {
        width: '100%',
        height: '100%',
        left: '0px',
        top: '0px',
      };
    }, [windowWidth, windowHeight, videoRef, readyCamera]);

    return (
      <>
        <div className="o-camera_back" onClick={onBack}>
          <Icon iconName="close" size="28" />
        </div>
        <div
          className="o-camera"
          style={{ maxHeight: `${windowHeight}px` }}
        >
          <div className="o-camera_wrapper" style={{ ...frame }}>
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
            <div className="o-camera_line" />
          </div>
          <video
            id={`video-${name}`}
            ref={videoRef}
            className="o-camera_video"
            autoPlay
            playsInline
            controls={false}
            muted
          />
          <canvas
            id={`canvas-${name}`}
            ref={canvasRef}
          />
          <div className="o-camera_capture">
            <div className="o-camera_capture-button">
              <Button onClick={handleCapture} />
            </div>
          </div>
          {preview && (
            <div className="o-camera_preview" style={{ backgroundImage: `url(${preview})` }}>
              <div className="o-camera_capturePreview">
                <div className="o-camera_capturePreview-button">
                  <Button disabled={loading} onClick={closeReview}>
                    <Typography.Text>
                      {isSingle ? 'Chụp lại' : 'Chụp tiếp'}
                    </Typography.Text>
                  </Button>
                </div>
                <div className="o-camera_capturePreview-button">
                  <Button
                    type="primary"
                    disabled={loading}
                    onClick={() => !loading && (isConfirm ? setIsOpen(true) : handleSubmit())}
                  >
                    <Typography.Text style={{ color: 'white' }}>
                      Đi tiếp
                    </Typography.Text>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        <Modal open={isOpen} footer={false}>
          <Typography.Title level={4}>{notifySuccess?.title}</Typography.Title>
          <Typography.Text className="u-mb-16">{notifySuccess?.title}</Typography.Text>
          <Space direction="horizontal">
            <Button onClick={handleReset}>Chụp lại</Button>
            <Button type="primary" onClick={handleSubmit} disabled={loading}>Đồng ý</Button>
          </Space>
        </Modal>
        <Modal open={errRequest} footer={false} width={400}>
          <Typography.Text className="u-mb-16">
            Chức năng này cần cho phép truy cập camera
            <br />
            <br />
            Hướng dẫn bật truy cập camera:
            <br />
            iOS: Vào cài đặt &gt; Safari &gt; Camera &gt; Chọn Cho phép
            <br />
          </Typography.Text>
          <Space direction="horizontal">
            <Button
              type="primary"
              onClick={() => {
                setErrRequest(false);
              }}
              disabled={loading}
            >
              Ok
            </Button>
          </Space>
        </Modal>
        <Modal open={isSupport} footer={false} width={400}>
          <Typography.Text className="u-mb-16">
            Trình duyệt hoặc thiết bị của bạn
            <br />
            không hỗ trợ camera để thực hiện tính năng này.
            <br />
            Vui lòng kiểm tra lại.
          </Typography.Text>
          <Space direction="horizontal">
            <Button
              type="primary"
              onClick={() => {
                setIsSupport(false);
                onBack();
              }}
              disabled={loading}
            >
              Ok
            </Button>
          </Space>
        </Modal>
      </>
    );
  }
);

export default CameraCapture;
